"use client";

import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, ChevronDown, ChevronUp, FileText, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  deleteSavedAddress,
  listMonitorEvents,
  listSavedAddresses,
  PlanGateError,
  updateSavedAddress,
  type MonitorEventItem,
  type SavedAddress,
} from "@/lib/retrieval";
import { cn } from "@/lib/utils";

/**
 * The saved-addresses panel: each row is an address under management with its
 * monitoring toggle, detected-change events, report link, and removal. The
 * quota line states the plan's limit plainly — the number comes from the API,
 * which reads it from config.
 */
export function SavedAddresses({ refreshKey = 0 }: { refreshKey?: number }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<SavedAddress[]>([]);
  const [limit, setLimit] = useState<number | null>(null);
  const [gated, setGated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openEvents, setOpenEvents] = useState<Record<number, MonitorEventItem[] | "loading">>({});
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<number | null>(null);
  const { showToast } = useToast();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listSavedAddresses();
      setRows(payload.addresses);
      setLimit(payload.limit);
      setGated(false);
    } catch (err) {
      if (err instanceof PlanGateError) {
        setGated(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Signed-out visitors get no fetch and no panel: firing the authenticated
  // list call with no token guaranteed a 401 on every anonymous dashboard
  // load, and the empty-state copy invited saving they can't do (2026-08-29).
  useEffect(() => {
    if (!user) return;
    void refresh();
  }, [refresh, refreshKey, user]);

  async function toggleMonitoring(row: SavedAddress) {
    try {
      const updated = await updateSavedAddress(row.id, {
        monitoring_enabled: !row.monitoring_enabled,
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function remove(row: SavedAddress) {
    // Two-step inline confirm: deleting drops the monitoring baseline and
    // event history with no undo, so one stray click must not do it.
    if (confirmingRemoveId !== row.id) {
      setConfirmingRemoveId(row.id);
      window.setTimeout(
        () => setConfirmingRemoveId((cur) => (cur === row.id ? null : cur)),
        4000,
      );
      return;
    }
    setConfirmingRemoveId(null);
    try {
      await deleteSavedAddress(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      showToast("Address removed.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Removal failed.");
    }
  }

  async function toggleEvents(row: SavedAddress) {
    if (openEvents[row.id]) {
      setOpenEvents((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      return;
    }
    setOpenEvents((prev) => ({ ...prev, [row.id]: "loading" }));
    try {
      const events = await listMonitorEvents(row.id);
      setOpenEvents((prev) => ({ ...prev, [row.id]: events }));
    } catch {
      setOpenEvents((prev) => ({ ...prev, [row.id]: [] }));
    }
  }

  if (!user) return null;

  if (gated) {
    return (
      <div className="panel p-6">
        <p className="overline">Saved addresses</p>
        <p className="mt-2 text-body-sm text-muted">
          Save addresses and get an email when a rule Prosa tracks for them
          changes — part of the paid plan.{" "}
          <a href="/pricing" className="text-primary hover:underline">
            See pricing
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="panel p-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="overline">Saved addresses</p>
        {limit !== null ? (
          <span className="cite text-muted">
            {rows.length} of {limit}
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-3 text-body-sm text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-body-sm text-muted">
          Nothing saved yet. Run a lookup above and choose “Save &amp;
          monitor” — Prosa will re-check its rules daily and email you when a
          change is detected.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {rows.map((row) => {
            const events = openEvents[row.id];
            return (
              <li key={row.id} className="py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">
                      {row.label || row.normalized_address}
                    </p>
                    <p className="cite text-muted">{row.topic}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleMonitoring(row)}
                      title={
                        row.monitoring_enabled
                          ? "Monitoring on — click to pause"
                          : "Monitoring paused — click to resume"
                      }
                    >
                      {row.monitoring_enabled ? (
                        <Bell className="h-4 w-4 text-primary" />
                      ) : (
                        <BellOff className="h-4 w-4 text-muted" />
                      )}
                    </Button>
                    <Button asChild variant="ghost" size="sm" title="Compliance report">
                      <a
                        href={`/report?address=${encodeURIComponent(row.address)}&topic=${encodeURIComponent(row.topic)}`}
                      >
                        <FileText className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleEvents(row)}
                      title="Detected changes"
                    >
                      {events ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(row)}
                      title={
                        confirmingRemoveId === row.id
                          ? "Click again to confirm removal"
                          : "Remove"
                      }
                    >
                      {confirmingRemoveId === row.id ? (
                        <span className="text-body-sm text-warn">Remove?</span>
                      ) : (
                        <Trash2 className="h-4 w-4 text-muted" />
                      )}
                    </Button>
                  </div>
                </div>

                {events ? (
                  <div className="mt-2 rounded-[8px] border border-border bg-paper/60 px-3 py-2.5">
                    {events === "loading" ? (
                      <p className="text-body-sm text-muted">Loading events…</p>
                    ) : events.length === 0 ? (
                      <p className="text-body-sm text-muted">
                        No changes detected yet. Monitoring reports changes
                        Prosa detects on official sources at check time.
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {events.map((ev) => (
                          <li key={ev.id} className="text-body-sm">
                            <span
                              className={cn(
                                "badge mr-1.5",
                                ev.change_type === "changed" || ev.change_type === "stale"
                                  ? "badge-warn"
                                  : undefined,
                              )}
                            >
                              {ev.change_type}
                            </span>
                            <span className="cite text-ink">{ev.citation}</span>{" "}
                            <span className="text-muted">{ev.detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
